import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Mail, MessageSquare, FileText, Send, Loader2, Plus, Trash2,
  Edit2, Globe, Check, ChevronDown, Clock, AlertTriangle
} from 'lucide-react';

const API_URL = 'https://workvanapp.com/api/recruitment-send';

const ADMIN_EMAILS = ['josh@workvanapp.com', 'trent@workvanapp.com'];

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// --- Styles ---
const st = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
  },
  panel: {
    background: '#1a1a1a', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '640px',
    maxHeight: '92vh', overflow: 'auto', padding: '20px', position: 'relative'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'
  },
  title: { margin: 0, color: '#fff', fontSize: '17px', fontWeight: 700 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
  tabs: {
    display: 'flex', gap: 0, borderRadius: '8px', overflow: 'hidden',
    border: '1px solid #2a2a2a', marginBottom: '16px'
  },
  tab: (active) => ({
    flex: 1, padding: '10px 12px', textAlign: 'center', fontSize: '13px', fontWeight: 600,
    background: active ? '#ff6b35' : '#0a0a0a', color: active ? '#fff' : '#888',
    border: 'none', cursor: 'pointer', transition: 'all 0.2s',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
  }),
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#aaa', marginBottom: '4px', marginTop: '12px' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#0a0a0a', color: '#e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  inputReadonly: {
    width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#111', color: '#888', fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  select: {
    width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#0a0a0a', color: '#e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    appearance: 'none', cursor: 'pointer'
  },
  textarea: {
    width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #2a2a2a',
    background: '#0a0a0a', color: '#e0e0e0', fontSize: '14px', outline: 'none',
    minHeight: '120px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
  },
  sendBtn: (disabled) => ({
    width: '100%', padding: '12px', borderRadius: '8px', border: 'none', fontSize: '14px',
    fontWeight: 600, color: '#fff', background: disabled ? '#333' : '#ff6b35',
    cursor: disabled ? 'not-allowed' : 'pointer', marginTop: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    opacity: disabled ? 0.5 : 1, transition: 'all 0.2s'
  }),
  charCount: (over) => ({
    fontSize: '12px', color: over ? '#e74c3c' : '#666', textAlign: 'right', marginTop: '4px'
  }),
  msgCard: (type) => ({
    padding: '10px 12px', background: '#0a0a0a', borderRadius: '8px',
    border: '1px solid #2a2a2a', marginBottom: '8px',
    borderLeft: `3px solid ${type === 'email' ? '#3498db' : '#27ae60'}`
  }),
  msgType: (type) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
    background: type === 'email' ? '#3498db22' : '#27ae6022',
    color: type === 'email' ? '#3498db' : '#27ae60',
    border: `1px solid ${type === 'email' ? '#3498db44' : '#27ae6044'}`
  }),
  templateCard: {
    padding: '12px', background: '#0a0a0a', borderRadius: '8px',
    border: '1px solid #2a2a2a', marginBottom: '8px'
  },
  templateBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
    background: '#9b59b622', color: '#9b59b6', border: '1px solid #9b59b644', marginLeft: '8px'
  },
  smallBtn: (bg) => ({
    padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: 600,
    color: '#fff', background: bg, cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', gap: '4px'
  }),
  error: { color: '#e74c3c', fontSize: '13px', marginTop: '8px' },
  success: { color: '#27ae60', fontSize: '13px', marginTop: '8px' },
  divider: { height: '1px', background: '#2a2a2a', margin: '16px 0' },
  noConsent: {
    textAlign: 'center', padding: '30px 20px', color: '#888', fontSize: '14px'
  }
};

export default function ApplicantMessaging({ applicant, userEmail, onClose }) {
  const [tab, setTab] = useState('email');

  // Email state
  const [fromEmail, setFromEmail] = useState(() => {
    if (ADMIN_EMAILS.includes(userEmail)) return userEmail;
    return ADMIN_EMAILS[0];
  });
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // SMS state
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsSuccess, setSmsSuccess] = useState('');

  // Messages
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [tplName, setTplName] = useState('');
  const [tplType, setTplType] = useState('email');
  const [tplSubject, setTplSubject] = useState('');
  const [tplBody, setTplBody] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Load messages
  const loadMessages = useCallback(async () => {
    if (!applicant?.id) return;
    setLoadingMessages(true);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-messages', applicantId: applicant.id })
      });
      const data = await res.json();
      if (data.messages) setMessages(data.messages);
    } catch (e) {
      console.error('Failed to load messages:', e);
    } finally {
      setLoadingMessages(false);
    }
  }, [applicant?.id]);

  // Load templates
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-templates', userEmail })
      });
      const data = await res.json();
      if (data.templates) setTemplates(data.templates);
    } catch (e) {
      console.error('Failed to load templates:', e);
    } finally {
      setLoadingTemplates(false);
    }
  }, [userEmail]);

  useEffect(() => {
    loadMessages();
    loadTemplates();
  }, [loadMessages, loadTemplates]);

  // --- Send Email ---
  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      setEmailError('Subject and body are required');
      return;
    }
    setEmailSending(true);
    setEmailError('');
    setEmailSuccess('');
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send-email',
          to: applicant.email,
          from: fromEmail,
          subject: emailSubject,
          bodyText: emailBody,
          applicantId: applicant.id,
          sentBy: userEmail
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send email');
      setEmailSuccess('Email sent successfully');
      setEmailSubject('');
      setEmailBody('');
      loadMessages();
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailSending(false);
    }
  };

  // --- Send SMS ---
  const handleSendSms = async () => {
    if (!smsBody.trim()) {
      setSmsError('Message body is required');
      return;
    }
    setSmsSending(true);
    setSmsError('');
    setSmsSuccess('');
    try {
      const phone = applicant.phone?.replace(/\D/g, '');
      const to = phone?.startsWith('1') ? '+' + phone : '+1' + phone;
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send-sms',
          to,
          smsBody: smsBody,
          applicantId: applicant.id,
          sentBy: userEmail
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send SMS');
      setSmsSuccess('SMS sent successfully');
      setSmsBody('');
      loadMessages();
    } catch (err) {
      setSmsError(err.message);
    } finally {
      setSmsSending(false);
    }
  };

  // --- Template actions ---
  const handleSaveTemplate = async () => {
    if (!tplName.trim() || !tplBody.trim()) return;
    setSavingTemplate(true);
    try {
      const payload = {
        name: tplName,
        type: tplType,
        subject: tplType === 'email' ? tplSubject : null,
        body: tplBody,
        created_by: userEmail,
        ...(editingTemplate ? { id: editingTemplate.id } : {})
      };
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save-template', template: payload })
      });
      if (!res.ok) throw new Error('Failed to save template');
      resetTemplateForm();
      loadTemplates();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete-template', templateId: id })
      });
      loadTemplates();
    } catch (e) {
      console.error(e);
    }
  };

  const handleShareTemplate = async (id) => {
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'share-template', templateId: id })
      });
      loadTemplates();
    } catch (e) {
      console.error(e);
    }
  };

  const applyTemplate = (tpl) => {
    if (tpl.type === 'email' || tab === 'email') {
      setEmailSubject(tpl.subject || '');
      setEmailBody(tpl.body || '');
      setTab('email');
    } else {
      setSmsBody(tpl.body || '');
      setTab('sms');
    }
  };

  const startEditTemplate = (tpl) => {
    setEditingTemplate(tpl);
    setTplName(tpl.name);
    setTplType(tpl.type || 'email');
    setTplSubject(tpl.subject || '');
    setTplBody(tpl.body);
    setShowNewTemplate(true);
  };

  const resetTemplateForm = () => {
    setShowNewTemplate(false);
    setEditingTemplate(null);
    setTplName('');
    setTplType('email');
    setTplSubject('');
    setTplBody('');
  };

  // SMS character counting
  const smsSegments = Math.max(1, Math.ceil(smsBody.length / 160));
  const smsCharsLeft = smsSegments * 160 - smsBody.length;

  // Filter templates by tab context
  const emailTemplates = templates.filter(t => t.type === 'email');
  const smsTemplates = templates.filter(t => t.type === 'sms');

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={st.header}>
          <h3 style={st.title}>Message {applicant.name}</h3>
          <button onClick={onClose} style={st.closeBtn}>
            <X size={20} color="#999" />
          </button>
        </div>

        {/* Tab bar */}
        <div style={st.tabs}>
          <button style={st.tab(tab === 'email')} onClick={() => setTab('email')}>
            <Mail size={14} /> Email
          </button>
          <button style={st.tab(tab === 'sms')} onClick={() => setTab('sms')}>
            <MessageSquare size={14} /> SMS
          </button>
          <button style={st.tab(tab === 'templates')} onClick={() => setTab('templates')}>
            <FileText size={14} /> Templates
          </button>
        </div>

        {/* ===== EMAIL TAB ===== */}
        {tab === 'email' && (
          <div>
            <label style={st.label}>From</label>
            <select style={st.select} value={fromEmail} onChange={(e) => setFromEmail(e.target.value)}>
              {ADMIN_EMAILS.map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>

            <label style={st.label}>To</label>
            <input style={st.inputReadonly} value={applicant.email || 'No email on file'} readOnly />

            {emailTemplates.length > 0 && (
              <>
                <label style={st.label}>Use Template</label>
                <select
                  style={st.select}
                  value=""
                  onChange={(e) => {
                    const tpl = emailTemplates.find(t => t.id === e.target.value);
                    if (tpl) applyTemplate(tpl);
                  }}
                >
                  <option value="">-- Select a template --</option>
                  {emailTemplates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_global ? ' (shared)' : ''}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label style={st.label}>Subject</label>
            <input
              style={st.input}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject..."
            />

            <label style={st.label}>Body</label>
            <textarea
              style={st.textarea}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Write your email..."
            />

            {emailError && <p style={st.error}>{emailError}</p>}
            {emailSuccess && <p style={st.success}>{emailSuccess}</p>}

            <button
              style={st.sendBtn(emailSending || !applicant.email || !emailSubject.trim() || !emailBody.trim())}
              onClick={handleSendEmail}
              disabled={emailSending || !applicant.email || !emailSubject.trim() || !emailBody.trim()}
            >
              {emailSending ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
              {emailSending ? 'Sending...' : 'Send Email'}
            </button>

            {/* Message history */}
            <div style={st.divider} />
            <MessageHistory messages={messages.filter(m => m.type === 'email')} loading={loadingMessages} />
          </div>
        )}

        {/* ===== SMS TAB ===== */}
        {tab === 'sms' && (
          <div>
            {!applicant.sms_consent ? (
              <div style={st.noConsent}>
                <AlertTriangle size={32} color="#f39c12" style={{ marginBottom: '12px' }} />
                <p style={{ margin: 0, fontWeight: 600, color: '#f39c12' }}>Applicant has not opted in to SMS</p>
                <p style={{ margin: '8px 0 0', color: '#666', fontSize: '13px' }}>
                  SMS consent must be given before messages can be sent.
                </p>
              </div>
            ) : (
              <>
                <label style={st.label}>To</label>
                <input style={st.inputReadonly} value={applicant.phone || 'No phone on file'} readOnly />

                {smsTemplates.length > 0 && (
                  <>
                    <label style={st.label}>Use Template</label>
                    <select
                      style={st.select}
                      value=""
                      onChange={(e) => {
                        const tpl = smsTemplates.find(t => t.id === e.target.value);
                        if (tpl) applyTemplate(tpl);
                      }}
                    >
                      <option value="">-- Select a template --</option>
                      {smsTemplates.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.is_global ? ' (shared)' : ''}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <label style={st.label}>Message</label>
                <textarea
                  style={{ ...st.textarea, minHeight: '100px' }}
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  placeholder="Write your SMS message..."
                  maxLength={480}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {smsSegments > 1 ? `${smsSegments} segments` : '1 segment'}
                  </span>
                  <span style={st.charCount(smsCharsLeft < 20)}>
                    {smsBody.length} / {smsSegments * 160} chars ({smsCharsLeft} left)
                  </span>
                </div>

                {smsError && <p style={st.error}>{smsError}</p>}
                {smsSuccess && <p style={st.success}>{smsSuccess}</p>}

                <button
                  style={st.sendBtn(smsSending || !applicant.phone || !smsBody.trim())}
                  onClick={handleSendSms}
                  disabled={smsSending || !applicant.phone || !smsBody.trim()}
                >
                  {smsSending ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                  {smsSending ? 'Sending...' : 'Send SMS'}
                </button>

                <div style={st.divider} />
                <MessageHistory messages={messages.filter(m => m.type === 'sms')} loading={loadingMessages} />
              </>
            )}
          </div>
        )}

        {/* ===== TEMPLATES TAB ===== */}
        {tab === 'templates' && (
          <div>
            {/* New / Edit template form */}
            {showNewTemplate ? (
              <div style={{ ...st.templateCard, borderColor: '#ff6b35' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 600, color: '#fff', fontSize: '14px' }}>
                    {editingTemplate ? 'Edit Template' : 'New Template'}
                  </span>
                  <button onClick={resetTemplateForm} style={st.closeBtn}>
                    <X size={16} color="#999" />
                  </button>
                </div>

                <label style={st.label}>Name</label>
                <input
                  style={st.input}
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  placeholder="Template name..."
                />

                <label style={st.label}>Type</label>
                <select style={st.select} value={tplType} onChange={(e) => setTplType(e.target.value)}>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>

                {tplType === 'email' && (
                  <>
                    <label style={st.label}>Subject</label>
                    <input
                      style={st.input}
                      value={tplSubject}
                      onChange={(e) => setTplSubject(e.target.value)}
                      placeholder="Email subject..."
                    />
                  </>
                )}

                <label style={st.label}>Body</label>
                <textarea
                  style={st.textarea}
                  value={tplBody}
                  onChange={(e) => setTplBody(e.target.value)}
                  placeholder="Template body..."
                />

                <button
                  style={st.sendBtn(savingTemplate || !tplName.trim() || !tplBody.trim())}
                  onClick={handleSaveTemplate}
                  disabled={savingTemplate || !tplName.trim() || !tplBody.trim()}
                >
                  {savingTemplate ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={16} />}
                  {savingTemplate ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Save Template')}
                </button>
              </div>
            ) : (
              <button
                style={{ ...st.smallBtn('#ff6b35'), marginBottom: '12px', padding: '10px 16px', fontSize: '13px' }}
                onClick={() => setShowNewTemplate(true)}
              >
                <Plus size={14} /> New Template
              </button>
            )}

            {/* Template list */}
            {loadingTemplates ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Loader2 size={20} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : templates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#666', fontSize: '14px' }}>
                No templates yet. Create one to get started.
              </div>
            ) : (
              templates.map(tpl => (
                <div key={tpl.id} style={st.templateCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#fff', fontSize: '14px' }}>{tpl.name}</span>
                        <span style={{
                          ...st.msgType(tpl.type),
                          marginLeft: '8px'
                        }}>
                          {tpl.type}
                        </span>
                        {tpl.is_global && <span style={st.templateBadge}>Shared</span>}
                      </div>
                      {tpl.subject && (
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#888' }}>
                          Subject: {tpl.subject}
                        </p>
                      )}
                      <p style={{
                        margin: '6px 0 0', fontSize: '13px', color: '#aaa', lineHeight: '1.4',
                        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                      }}>
                        {tpl.body}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button style={st.smallBtn('#ff6b35')} onClick={() => applyTemplate(tpl)}>
                      Use
                    </button>
                    {tpl.created_by === userEmail && (
                      <>
                        <button style={st.smallBtn('#555')} onClick={() => startEditTemplate(tpl)}>
                          <Edit2 size={12} /> Edit
                        </button>
                        {!tpl.is_global && (
                          <button style={st.smallBtn('#9b59b6')} onClick={() => handleShareTemplate(tpl.id)}>
                            <Globe size={12} /> Share
                          </button>
                        )}
                        <button style={st.smallBtn('#e74c3c')} onClick={() => handleDeleteTemplate(tpl.id)}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Message History Sub-component ---
function MessageHistory({ messages, loading }) {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Loader2 size={18} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', padding: '12px 0' }}>
        No messages sent yet
      </p>
    );
  }

  return (
    <div>
      <p style={{ fontSize: '13px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>
        Sent Messages ({messages.length})
      </p>
      {messages.map(msg => (
        <div key={msg.id} style={st.msgCard(msg.type)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={st.msgType(msg.type)}>{msg.type}</span>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {msg.sent_by}
              </span>
            </div>
            <span style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Clock size={11} /> {relativeTime(msg.created_at)}
            </span>
          </div>
          {msg.subject && (
            <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: '#ccc' }}>
              {msg.subject}
            </p>
          )}
          <p style={{ margin: 0, fontSize: '13px', color: '#aaa', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
            {msg.body}
          </p>
          <div style={{ fontSize: '11px', color: '#555', marginTop: '6px' }}>
            {msg.from_address} → {msg.to_address}
          </div>
        </div>
      ))}
    </div>
  );
}
